import { useApi } from '@/hooks/useApi';
import { axiosInstance } from '@/utility';
import { useApiUrl, useCreate, useDelete, useOne } from '@refinedev/core';
import { isArray } from 'lodash';

const RESOURCE = 'orders';

export const useOrderService = () => {
  const { mutate: createMutate, isLoading: loadingCreate } = useCreate();
  const { mutate: deleteMutate, isLoading: loadingDelete } = useDelete();
  const { sendBasicRequest } = useApi();
  const apiUrl = useApiUrl();

  function getById(id: string) {
    // TODO: React Hook is called in function
    return useOne<any>({
      resource: RESOURCE,
      id,
    });
  }

  async function create(payload: IOrderCreatePayload): Promise<any> {
    return new Promise((resolve, reject) => {
      createMutate({
        resource: `${RESOURCE}/create`,
        values: {
          ...payload,
          order_date_time_utc: isArray(payload?.order_date_time_utc)
            ? payload?.order_date_time_utc?.[0] : payload?.order_date_time_utc,
        },
      }, {
        onSuccess: (response) => resolve(response),
        onError: (error) => reject(error),
      });
    });
  }

  async function calculate(payload: CalculateParcelPricePayload): Promise<any> {
    return new Promise((resolve, reject) => {
      createMutate({
        resource: 'calculation-price',
        values: payload,
        dataProviderName: 'masterdata',
      }, {
        onSuccess: (response) => resolve(response),
        onError: (error) => reject(error),
      });
    });
  }

  async function cancelOrder(payload: CancelOrderPayload) {
    return new Promise((resolve, reject) => {
      createMutate({
        resource: `${RESOURCE}/cancel`,
        values: payload,
      }, {
        onSuccess: resolve,
        onError: reject,
      });
    });
  }

  async function awbData(awb: string) {
    try {
      const response = await sendBasicRequest(`/awbs/detail/${awb}`, 'GET');

      if (response?.message) throw new Error(response?.message);

      return response.data;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async function importOrder(payload: FormData) {
    try {
      const response = await sendBasicRequest(
        `/${RESOURCE}/import/create`,
        'POST',
        payload,
      );
      if (response?.message) throw new Error(response?.message);

      return response.data;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async function previewPODOrder(fileName: string) {
    try {
      const response = await sendBasicRequest(`/files/retrieve?file_name=${fileName}&file_path=${fileName}`, 'GET', {}, {
        responseType: 'blob',
      });

      if (response?.message) throw new Error(response?.message);

      return response;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async function exportOrder(payload: any): Promise<any> {
    const response = await axiosInstance({
      url: `${apiUrl}/${RESOURCE}/export-xls`,
      method: 'POST',
      responseType: 'blob',
      data: payload,
    });

    if (response.data?.message) throw new Error(response.data?.message);

    return response;
  }

  return {
    loadingCreate,
    loadingDelete,
    getById,
    create,
    calculate,
    cancelOrder,
    awbData,
    exportOrder,
    importOrder,
    previewPODOrder,
  };
};

export default useOrderService;
